import { ArrowLeft, ArrowRight } from "lucide-react";
import type React from "react";
import type { ScrollStrip } from "../../lib/use-scroll-strip";
import { cn } from "../../lib/utils";

/** What a strip's arrows need: the strip, its track, and what its tiles are. */
interface ScrollStripArrowsProps {
  strip: ScrollStrip;
  /** The track's id, for aria-controls. */
  trackId: string;
  /** What the tiles are, plural ("photos"), for the button labels. */
  itemsLabel: string;
  /** Whether they sit on a dark band. */
  onDark?: boolean;
}

const
  ARROW_CLASS =
    "jobsite:flex jobsite:size-11 jobsite:items-center jobsite:justify-center jobsite:border-2 jobsite:transition-colors jobsite:disabled:cursor-default jobsite:disabled:opacity-35 jobsite:focus-visible:outline-none jobsite:focus-visible:ring-[3px] jobsite:focus-visible:ring-ring",
  ARROW_ON_DARK_CLASS =
    "jobsite:border-primary-foreground jobsite:bg-primary-foreground jobsite:text-primary jobsite:enabled:hover:bg-accent jobsite:enabled:hover:border-accent jobsite:enabled:hover:text-accent-foreground",
  ARROW_ON_LIGHT_CLASS =
    "jobsite:border-foreground jobsite:bg-transparent jobsite:text-foreground jobsite:enabled:hover:bg-foreground jobsite:enabled:hover:text-background",

  /**
   * One arrow: pages the strip back or forward, and is disabled at that end.
   * @param {object} props The arrow.
   * @param {-1 | 1} props.direction Back (-1) or forward (1).
   * @param {ScrollStripArrowsProps} props.arrows The strip it moves and how it looks.
   * @returns {React.JSX.Element} The button.
   */
  StripArrow = ({ direction, arrows }: { direction: -1 | 1; arrows: ScrollStripArrowsProps }): React.JSX.Element => {
    const { strip, trackId, itemsLabel, onDark = false } = arrows,
      isBack = direction === -1,
      Icon = isBack ? ArrowLeft : ArrowRight;
    return (
      <button
        type="button"
        aria-controls={trackId}
        aria-label={`${isBack ? "Previous" : "Next"} ${itemsLabel}`}
        disabled={isBack ? !strip.canScrollBack : !strip.canScrollForward}
        onClick={() => {
          strip.scrollByPage(direction);
        }}
        // Lets the editor canvas run the click instead of selecting the section.
        data-sp-interactive=""
        className={cn(ARROW_CLASS, onDark ? ARROW_ON_DARK_CLASS : ARROW_ON_LIGHT_CLASS)}
        style={{ borderRadius: "var(--jobsite-radius)" }}
      >
        <Icon className="jobsite:size-5" aria-hidden="true" />
      </button>
    );
  },

  /**
   * Previous and next buttons for a strip. Hidden on phones, where swiping is
   * the gesture, and kept in place but invisible when everything already fits.
   * @param {ScrollStripArrowsProps} props The arrows.
   * @returns {React.JSX.Element} The two buttons.
   */
  ScrollStripArrows = (props: ScrollStripArrowsProps): React.JSX.Element => (
    <div
      className={cn(
        "jobsite:hidden jobsite:shrink-0 jobsite:gap-2 jobsite:sm:flex",
        !props.strip.canScrollBack && !props.strip.canScrollForward && "jobsite:invisible",
      )}
    >
      <StripArrow direction={-1} arrows={props} />
      <StripArrow direction={1} arrows={props} />
    </div>
  );

export { ScrollStripArrows };
